import { useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table/table";
import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { getTeamAttendanceQueryOptions } from "@/config/queries/teamleader/tl-query-options";

const istDay = (d = new Date()) => new Date(d.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

const BADGE: Record<string, { className?: string; variant?: "secondary" | "outline" | "destructive" }> = {
  present: { className: "bg-green-600 hover:bg-green-600" },
  absent: { variant: "destructive" },
  leave: { className: "bg-amber-500 text-black hover:bg-amber-500" },
  holiday: { variant: "outline" },
  unmarked: { variant: "secondary" },
};

export default function TeamLeaderAttendancePage() {
  const key = Cookies.get("key") as string;
  const [day, setDay] = useState(istDay());
  const date = useMemo(() => new Date(`${day}T12:00:00+05:30`), [day]);
  const { data, isFetching, refetch } = useQuery(getTeamAttendanceQueryOptions(key, date));

  const rows = data?.employees ?? [];
  const present = rows.filter((r) => r.state === "present").length;
  const absent = rows.filter((r) => r.state === "absent").length;
  const leave = rows.filter((r) => r.state === "leave").length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium">Present / absent</h1>
          <p className="text-sm text-muted-foreground">
            {present} present · {absent} absent · {leave} on leave
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={day}
            max={istDay()}
            onChange={(e) => setDay(e.target.value)}
            className="w-[170px]"
            data-testid="tl-att-date"
          />
          <Button size="sm" variant="outline" onClick={() => refetch()} className="gap-2">
            <RefreshCw className={isFetching ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table data-testid="tl-attendance-table">
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>On this date</TableHead>
              <TableHead className="text-right">Present this month</TableHead>
              <TableHead className="text-right">Absent this month</TableHead>
              <TableHead className="text-right">On leave this month</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  {isFetching ? "Loading…" : "Nothing to show for this date."}
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const b = BADGE[r.state] ?? {};
              return (
                <TableRow key={r.employee._id} data-testid="tl-att-row">
                  <TableCell className="font-medium">{r.employee.name}</TableCell>
                  <TableCell>
                    <Badge variant={b.variant} className={b.className} data-testid="tl-att-state">
                      {r.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{r.monthToDate.present}</TableCell>
                  <TableCell className="text-right">{r.monthToDate.absent}</TableCell>
                  <TableCell className="text-right">{r.monthToDate.leave}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
